import 'react-tabs/style/react-tabs.css'
import 'leaflet/dist/leaflet.css'

import styled from '@emotion/styled'
import { Button } from 'app/components/Common/Button'
import { Separator } from 'app/components/Common/Separator'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { useVocabularyData } from 'app/utils/vocabulary'
import L from 'leaflet'
import { uniq } from 'lodash'
import React, { memo, useEffect, useState } from 'react'
import LazyLoad from 'react-lazyload'
import { MapContainer, Marker, TileLayer } from 'react-leaflet'
import Fade from 'react-reveal/Fade'
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs'

import { Poi, Props as PoiProps } from './Poi'
import { TabIcon } from './TabIcon'

export interface Props {
  directions?: { [key: string]: any }
  googleMapsURL?: string
  image?: string
  languageCode: string
  logo?: string
  longitude?: string
  latitude?: string
  poiList?: PoiProps[]
  siteName?: string
  title?: string
  zoom?: string
}

export const Directions = memo(function Directions({
  directions,
  googleMapsURL,
  image,
  logo,
  languageCode,
  longitude,
  latitude,
  poiList,
  siteName,
  title,
  zoom,
}: Props) {
  const [markerIcon, setMarkerIcon] = useState(L.divIcon)

  const [modalOpen, setModal] = useState(null)
  const openModal = (e: any) => {
    const {
      target: {
        dataset: { modal },
      },
    } = e

    if (modal) setModal(modal)
  }

  useEffect(() => {
    setMarkerIcon(
      L.divIcon({
        iconSize: [55, 80],
        iconAnchor: [27, 80],
        html: `<img src="${logo}" alt="${siteName}" width="33" height="31" /><svg xmlns="http://www.w3.org/2000/svg" width="55.007" height="80" viewBox="0 0 55.007 80"><path d="M383.218,354.361a27.532,27.532,0,0,0-27.505,27.5v.1a30.507,30.507,0,0,0,4.216,15.467L381,433.2a2.347,2.347,0,0,0,2.016,1.157h.008a2.346,2.346,0,0,0,2.016-1.143l21.36-35.711a30.5,30.5,0,0,0,4.323-15.65A27.533,27.533,0,0,0,383.218,354.361Z" transform="translate(-355.713 -354.361)"/></svg><span>${siteName}</span>`,
        className: 'main-icon',
      }),
    )
  }, [])

  return (
    <Container>
      <Separator />
      <Fade bottom distance="3.75rem">
        <>
          <Head>
            <Label>{useVocabularyData('around', languageCode)}</Label>
          </Head>
          <Center>
            <Wrapper row wrap stretch>
              <LeftSide>
                {image ? (
                  <LazyLoad>
                    <Image style={{ backgroundImage: `url(${image})` }} />
                  </LazyLoad>
                ) : null}
                {poiList ? (
                  <PoiList>
                    {uniq(poiList).map((item, index) => (
                      <Poi
                        className={
                          modalOpen === `${index}` ? 'active' : undefined
                        }
                        dataModal={index}
                        key={index}
                        {...item}
                        onClick={(e: any) => {
                          openModal(e)
                        }}
                      />
                    ))}
                  </PoiList>
                ) : null}
              </LeftSide>
              <Map>
                {typeof window !== undefined && latitude && longitude ? (
                  <MapContainer
                    center={[Number(latitude), Number(longitude)]}
                    dragging={L.Browser && L.Browser.mobile ? false : true}
                    zoom={Number(zoom)}
                    scrollWheelZoom={false}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                    />
                    <Marker
                      icon={markerIcon}
                      position={[Number(latitude), Number(longitude)]}
                    />
                    {poiList
                      ? uniq(poiList).map((item, index) => (
                          <Marker
                            key={index}
                            icon={
                              typeof window !== 'undefined'
                                ? L.divIcon({
                                    iconSize: [24, 36],
                                    iconAnchor: [12, 36],
                                    html: '<svg xmlns="http://www.w3.org/2000/svg" width="24.749" height="36" viewBox="0 0 24.749 36"><path d="M92.379,0A12.389,12.389,0,0,0,80,12.375v.047a13.729,13.729,0,0,0,1.9,6.961l9.478,16.1a1.057,1.057,0,0,0,.907.521h0a1.057,1.057,0,0,0,.907-.514l9.611-16.069a13.722,13.722,0,0,0,1.945-7.042A12.389,12.389,0,0,0,92.379,0ZM92.29,17.922A5.319,5.319,0,1,1,97.609,12.6a5.319,5.319,0,0,1-5.319,5.319Z" transform="translate(-80.004)"/></svg>',
                                    className:
                                      modalOpen === `${index}`
                                        ? 'active'
                                        : undefined,
                                  })
                                : undefined
                            }
                            position={[
                              Number(item.latitude),
                              Number(item.longitude),
                            ]}
                          />
                        ))
                      : null}
                  </MapContainer>
                ) : null}
                {googleMapsURL ? (
                  <GoogleMapsCTA
                    arrow
                    label={useVocabularyData(
                      'open-in-google-maps',
                      languageCode,
                    )}
                    URL={googleMapsURL}
                    rel="noreferrer"
                    target="_blank"
                  />
                ) : null}
              </Map>
            </Wrapper>
          </Center>
          <Bottom>
            <HowToReach>
              {title ? <Title>{title}</Title> : null}
              {directions ? (
                <Tabs>
                  <TabList>
                    {directions.map((item: any, index: number) => (
                      <Tab key={index}>
                        <TabIcon icon={item.icon} />
                        {item.title}
                      </Tab>
                    ))}
                  </TabList>

                  {directions.map((item: any, index: number) => (
                    <TabPanel
                      key={index}
                      dangerouslySetInnerHTML={{ __html: item.description }}
                    />
                  ))}
                </Tabs>
              ) : null}
            </HowToReach>
          </Bottom>
        </>
      </Fade>
    </Container>
  )
})

const Container = styled.section`
  margin: 6rem auto 9.75rem;
  padding: 0 1.5rem;

  @media (max-width: 1023px) {
    margin: 3.75rem auto 7.5rem;
  }
`

const Head = styled.div`
  max-width: 53.5rem;
  border: 2px solid ${({ theme }) => theme.colors.variants.neutralLight3};
  border-bottom: 0;
  margin: auto;
  padding: 3.75rem 1.5rem;
  text-align: center;

  @media (max-width: 1023px) {
    max-width: 80%;
    padding: 2.25rem 1.5rem;
  }

  @media (max-width: 767px) {
    max-width: none;
    border: 0;
    padding: 0;
  }
`

const Label = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark4};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.375rem;
  font-weight: 500;
  line-height: 2.9375rem;
`

const Center = styled.div`
  position: relative;
  &:before {
    content: '';
    width: 100%;
    height: 50%;
    background: ${({ theme }) => theme.colors.variants.neutralLight3};
    position: absolute;
    bottom: 0;
    left: 0;
  }

  @media (max-width: 767px) {
    margin-top: 3rem;
    &:before {
      display: none;
    }
  }
`

const Wrapper = styled(FlexBox)`
  margin: auto;
`

const LeftSide = styled.div`
  width: 38%;
  padding: 3rem;
  position: relative;

  @media (max-width: 767px) {
    display: none;
  }
`

const Image = styled.span`
  width: 100%;
  height: 100%;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  position: absolute;
  top: 0;
  left: 0;
  &:before {
    content: '';
    width: 100%;
    height: 100%;
    opacity: 0.7;
    background: ${({ theme }) => theme.colors.variants.primaryDark1};
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
  }
`

const PoiList = styled.div`
  height: calc(84vh - 7.5rem);
  max-height: 757px;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.variants.primaryDark1};
  padding: 3.75rem;
  position: relative;
  z-index: 2;
`

const Map = styled.div`
  width: 62%;
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  position: relative;

  @media (max-width: 767px) {
    width: 100%;
    padding-bottom: 80%;
    .leaflet-left {
      display: none;
    }
  }

  .leaflet-container {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }
  .leaflet-marker-icon {
    background: none;
    border: 0;

    @media (max-width: 767px) {
      display: none;
    }

    &.active,
    &.main-icon {
      svg {
        fill: ${({ theme }) => theme.colors.variants.primaryDark1};
      }
    }
    &.active {
      svg {
        animation: bounce 2s 1;
      }
    }
    &.main-icon {
      @media (max-width: 767px) {
        display: block;
      }
    }
    img {
      position: absolute;
      top: 12px;
      left: 10px;
    }
    svg {
      fill: ${({ theme }) => theme.colors.variants.neutralDark4};
    }
    span {
      width: 90px;
      color: ${({ theme }) => theme.colors.variants.primaryDark1};
      font-family: ${({ theme }) => theme.fontFamily.paragraph};
      font-size: 0.6875rem;
      font-weight: 700;
      letter-spacing: 0.05rem;
      line-height: 0.875rem;
      position: absolute;
      bottom: -3.125rem;
      left: 50%;
      text-align: center;
      text-transform: uppercase;
      transform: translateX(-50%);
    }
  }
  .leaflet-left {
    top: auto;
    bottom: 0;
    .leaflet-control {
      border: 0;
      border-radius: 0;
      margin: 0;
      a {
        width: 3rem;
        height: 3rem;
        line-height: 2.875rem;
        background: ${({ theme }) => theme.colors.variants.neutralLight4};
        color: ${({ theme }) => theme.colors.variants.neutralDark1};
        font-family: ${({ theme }) => theme.fontFamily.paragraph};
        font-weight: 100;
        font-size: 2.375rem;
        border: 0;
        border-radius: 0;
        transition: 0.3s ease-out;
        &.leaflet-control-zoom-in {
          order: 2;
        }
        &.leaflet-disabled {
          opacity: 0.6;
          pointer-events: none;
        }
        &:hover {
          background: ${({ theme }) => theme.colors.variants.neutralLight3};
        }
      }
    }
  }
  .leaflet-bottom {
    display: none;
  }
  .leaflet-popup {
    .leaflet-popup-content-wrapper {
      background: ${({ theme }) => theme.colors.variants.primaryDark1};
      box-shadow: none;
      border-radius: 0;
      color: ${({ theme }) => theme.colors.variants.neutralLight4};
      font-family: ${({ theme }) => theme.fontFamily.heading};
      font-size: 1rem;
      padding: 1rem;
    }
    .leaflet-popup-content {
      line-height: 1.5rem;
      margin: 0;
    }
    .leaflet-popup-tip {
      background: ${({ theme }) => theme.colors.variants.primaryDark1};
      box-shadow: none;
    }
    .leaflet-popup-close-button {
      display: none;
    }
  }

  @keyframes bounce {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-1.875rem);
    }
    60% {
      transform: translateY(-0.9375rem);
    }
  }
`

const GoogleMapsCTA = styled(Button)`
  background: ${({ theme }) => theme.colors.variants.neutralDark4};
  position: absolute;
  bottom: 0;
  right: 0;
  z-index: 400;

  @media (max-width: 767px) {
    left: 0;
    justify-content: center;
  }
`

const Bottom = styled.div`
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
`

const HowToReach = styled.div`
  max-width: 54.25rem;
  margin: auto;
  padding: 3.75rem 1.5rem 7.5rem;
  overflow: hidden;
  position: relative;
  text-align: center;

  .react-tabs {
    font-family: ${({ theme }) => theme.fontFamily.paragraph};
    .react-tabs__tab-list {
      border: 0;
    }
    .react-tabs__tab {
      display: inline-flex;
      align-items: center;
      border: 0;
      color: ${({ theme }) => theme.colors.variants.neutralDark4};
      font-size: 0.8125rem;
      letter-spacing: 1px;
      line-height: 1.125rem;
      margin: 0 1.125rem;
      padding: 0;
      transition: 0.3s ease-out;
      &:last-of-type {
        &:after {
          display: none;
        }
      }
      &:after {
        content: '';
        width: 2px;
        height: 12px;
        margin-left: 2.25rem;
        background: ${({ theme }) => theme.colors.variants.neutralLight2};
      }
      &:focus {
        box-shadow: none;
      }
      &:hover {
        color: ${({ theme }) => theme.colors.variants.neutralDark1};
      }
      &.react-tabs__tab--selected {
        background: none;
        border-radius: 0;
        color: ${({ theme }) => theme.colors.variants.primaryDark1};
        svg {
          fill: ${({ theme }) => theme.colors.variants.primaryDark1};
        }
      }
    }
    .react-tabs__tab-panel {
      color: ${({ theme }) => theme.colors.variants.neutralDark4};
      font-size: 0.9375rem;
      line-height: 1.875rem;
      margin-top: 3.75rem;
      &.react-tabs__tab-panel--selected {
        animation: fadeInPanel 0.6s;
      }
    }
  }

  @media (max-width: 1023px) {
    padding: 3rem 1.5rem 3.75rem;
    .react-tabs {
      .react-tabs__tab {
        margin-top: 3rem;
      }
    }
  }

  @media (max-width: 767px) {
    .react-tabs {
      .react-tabs__tab {
        margin: 0 0.625rem;
        &:after {
          margin-left: 1rem;
        }
      }
      .react-tabs__tab-panel {
        margin-top: 2.25rem;
      }
    }
  }

  @keyframes fadeInPanel {
    from {
      opacity: 0;
      transform: translateY(2.25rem);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

const Title = styled.h3`
  color: ${({ theme }) => theme.colors.variants.neutralDark4};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.375rem;
  font-weight: 500;
  line-height: 2.9375rem;
  margin-bottom: 3.75rem;

  @media (max-width: 1023px) {
    margin-bottom: 3rem;
  }
`
